import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { blogAPI, issuesAPI } from '../../services/api';
import { useOrganization } from '../../context/OrganizationContext';
import './Home.css';

const stripHtml = (html) => (html ? html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim() : '');

const isRichHtml = (content) => content && /<[a-z][\s\S]*>/i.test(content);

const RichContent = ({ content, fallback, className = '' }) => {
  const value = content || fallback;
  if (!value) return null;

  if (isRichHtml(value)) {
    return <div className={`rich-content ${className}`.trim()} dangerouslySetInnerHTML={{ __html: value }} />;
  }

  return <div className={className}>{value}</div>;
};

const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
// Cloud Run caps responses at 32MB, so the hero video is served from GCS.
const DEFAULT_HERO_VIDEO =
  process.env.NODE_ENV === 'production'
    ? 'https://storage.googleapis.com/hksynergies-bucket/media/videos/intro.mp4'
    : `${publicUrl}/videos/intro.mp4`;

const Home = () => {
  const { organization, loading: orgLoading } = useOrganization();
  const [latestPosts, setLatestPosts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoFailed, setVideoFailed] = useState(false);
  const [useDefaultVideo, setUseDefaultVideo] = useState(false);
  const videoRef = useRef(null);

  const orgName = organization?.chinese_name || organization?.english_name || 'Hong Kong Synergies';
  const uploadedVideo = organization?.home_hero_video;
  const videoSrc = (!useDefaultVideo && uploadedVideo) || DEFAULT_HERO_VIDEO;
  const showVideo = !orgLoading && Boolean(videoSrc) && !videoFailed;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setUseDefaultVideo(false);
    setVideoFailed(false);
  }, [uploadedVideo]);

  const tryPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  useEffect(() => {
    if (!showVideo) return;
    tryPlay();
  }, [showVideo, videoSrc]);

  const handleVideoError = (event) => {
    const code = event.currentTarget.error?.code;
    if (code === 1) return;
    if (videoSrc !== DEFAULT_HERO_VIDEO) {
      setUseDefaultVideo(true);
      return;
    }
    setVideoFailed(true);
  };

  const fetchData = async () => {
    try {
      const [postsRes, servicesRes] = await Promise.all([
        blogAPI.getLatest(),
        issuesAPI.getAll(),
      ]);

      const postsData = Array.isArray(postsRes.data) ? postsRes.data : postsRes.data?.results || [];
      const servicesData = Array.isArray(servicesRes.data) ? servicesRes.data : servicesRes.data?.results || [];

      setLatestPosts(postsData.slice(0, 3));
      setServices(
        servicesData
          .filter((s) => s.is_active)
          .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
      );
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-page">
      {!videoFailed && (orgLoading || showVideo) && (
        <section className="hero-video-section" aria-label="介紹影片">
          {showVideo && (
            <video
              key={videoSrc}
              ref={videoRef}
              className="hero-video"
              src={videoSrc}
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="auto"
              onCanPlay={tryPlay}
              onError={handleVideoError}
            >
              您的瀏覽器不支援影片播放
            </video>
          )}
        </section>
      )}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <p className="hero-eyebrow">{orgName}</p>
            <h1>{organization?.title || '攜手共建更美好的香港'}</h1>
          </div>
        </div>
      </section>

      <section className="section mission-section">
        <div className="container">
          <h2 className="section-title">{organization?.home_mission_title || '我們的使命'}</h2>
          <div className="mission-text">
            <RichContent
              content={organization?.home_mission_text}
              fallback="我們致力於走進校園與社區，提供從預防性的情緒教育工作坊到專業的醫療學校轉介機制，全方位守護兒童及青少年的心理健康；同時開展全人能力培養與生涯規劃，並攜手家長推動正向親職教育，建立即時且低門檻的社會支援網絡。"
            />
          </div>
          <p className="mission-tagline">{organization?.home_mission_tagline || '以行動帶來改變'}</p>
        </div>
      </section>

      <section className="section services-overview">
        <div className="container">
          <h2 className="section-title">核心服務</h2>
          <div className="section-intro">
            <RichContent
              content={organization?.home_services_intro}
              fallback="四大核心服務體系，全方位守護兒童及青少年的身心健康"
            />
          </div>
          <div className="services-grid">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.slug || service.id}`}
                className="service-card card"
              >
                <h3>{service.title}</h3>
                <p>{stripHtml(service.description).slice(0, 100)}…</p>
                <span className="service-link">了解更多 →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ backgroundColor: 'var(--light-gray)' }}>
        <div className="container">
          <h2 className="section-title news-title">最新消息</h2>
          {latestPosts.length > 0 ? (
            <div className="blog-grid">
              {latestPosts.map((post) => (
                <div key={post.id} className="blog-card card">
                  {post.featured_image ? (
                    <img src={post.featured_image} alt={post.title} className="blog-image" />
                  ) : post.video ? (
                    <video src={post.video} className="blog-image" muted playsInline preload="metadata" />
                  ) : null}
                  <div className="blog-content">
                    <h3>{post.title}</h3>
                    <p className="blog-date">
                      {new Date(post.published_date).toLocaleDateString('zh-HK', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                    {post.summary && <p>{post.summary}</p>}
                    <Link to={`/blog/${post.id}`} className="btn btn-primary">
                      閱讀更多
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center">暫無消息</p>
          )}
          <div className="text-center mt-3">
            <Link to="/blog" className="btn btn-outline">
              查看所有文章
            </Link>
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container text-center">
          <h2>{organization?.home_cta_title || '攜手點燃希望'}</h2>
          <div className="cta-section-text">
            <RichContent
              content={organization?.home_cta_text}
              fallback="您的支持，讓我們得以持續守護兒童及青少年的身心健康，為未來鋪設更堅實的道路。"
            />
          </div>
          <Link to="/contact" className="btn btn-large btn-primary">
            聯絡我們
          </Link>
        </div>
      </section>
        </>
      )}
    </div>
  );
};

export default Home;
